import { useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import {
  DiscountTypeEnum,
  discountTypeLabels,
  promotionStatusColors,
  promotionStatusLabels,
  promotionTypeLabels,
} from "@/features/promotions";
import { apiClient, formatMoney, formatNumber, getErrorMessage } from "@/features/shared";
import { CustomDialogTitle, SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const Promotions = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [deleteDialogPromotion, setDeleteDialogPromotion] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setRowCount, paginationParams, dataGridPaginationProps, resetParams } =
    useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadPromotions = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("/promotions", {
        params: {
          ...paginationParams,
          ...(keyword && { name: keyword }),
          withPromotionCampaign: true,
          withPromotionRedemptionCount: true,
        },
      });
      setPromotions(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat promosi"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  const handleDelete = async () => {
    if (!deleteDialogPromotion) return;

    try {
      setIsDeleting(true);
      await apiClient.delete(`promotions/${deleteDialogPromotion.id}`);
      showSnackbar("Promosi berhasil dihapus", "success");
      setDeleteDialogPromotion(null);
      loadPromotions();
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal menghapus promosi"));
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDiscount = (row) => {
    if (row.discountType === DiscountTypeEnum.PERCENTAGE) {
      const maximum =
        row.maximumDiscountAmount != null
          ? ` (maks. ${formatMoney(row.maximumDiscountAmount)})`
          : "";
      return `${formatNumber(row.discountAmount)}%${maximum}`;
    }
    return formatMoney(row.discountAmount);
  };

  const toolbarContent = hasPermission(PermissionEnum.CREATE_PROMOTIONS) && (
    <Button variant="contained" onClick={() => navigate("create")}>
      Tambah
    </Button>
  );

  return (
    <PageContainer
      maxWidth="xl"
      breadcrumbs={[]}
      slotProps={{
        header: {
          slotProps: { toolbar: { children: toolbarContent } },
        },
      }}
    >
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama promosi"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={promotions}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 2,
              sortable: false,
            },
            {
              field: "promotionCampaign",
              headerName: "Kampanye",
              flex: 2,
              sortable: false,
              valueGetter: (value) => value?.name,
            },
            {
              field: "promotionType",
              headerName: "Tipe",
              flex: 1,
              sortable: false,
              valueFormatter: (value) => promotionTypeLabels[value],
            },
            {
              field: "discountType",
              headerName: "Diskon",
              flex: 1.5,
              sortable: false,
              renderCell: ({ row }) => (
                <span>
                  {discountTypeLabels[row.discountType]} — {formatDiscount(row)}
                </span>
              ),
            },
            {
              field: "promotionRedemptionCount",
              headerName: "Redeem",
              flex: 1,
              sortable: false,
              type: "number",
              valueFormatter: (value) => formatNumber(value ?? 0),
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={promotionStatusLabels[row.status]}
                  color={promotionStatusColors[row.status]}
                />
              ),
            },
            ...(hasPermission(PermissionEnum.READ_PROMOTIONS) ||
            hasPermission(PermissionEnum.UPDATE_PROMOTIONS) ||
            hasPermission(PermissionEnum.DELETE_PROMOTIONS)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    minWidth: 130,
                    getActions: (params) => [
                      ...(hasPermission(PermissionEnum.READ_PROMOTIONS)
                        ? [
                            <IconButton
                              key="view"
                              onClick={() => navigate(`${params.id}`)}
                              size="small"
                            >
                              <VisibilityIcon />
                            </IconButton>,
                          ]
                        : []),
                      ...(hasPermission(PermissionEnum.UPDATE_PROMOTIONS)
                        ? [
                            <IconButton
                              key="edit"
                              onClick={() => navigate(`${params.id}/edit`)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>,
                          ]
                        : []),
                      ...(hasPermission(PermissionEnum.DELETE_PROMOTIONS)
                        ? [
                            <IconButton
                              key="delete"
                              onClick={() =>
                                setDeleteDialogPromotion(params.row)
                              }
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>,
                          ]
                        : []),
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
        {!!deleteDialogPromotion && (
          <Dialog
            open
            onClose={() => !isDeleting && setDeleteDialogPromotion(null)}
            maxWidth="xs"
            fullWidth
          >
            <CustomDialogTitle
              title="Hapus Promosi"
              onClose={() => !isDeleting && setDeleteDialogPromotion(null)}
            />
            <DialogContent>
              <DialogContentText>
                Apakah Anda yakin ingin menghapus promosi{" "}
                <strong>{deleteDialogPromotion.name}</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogPromotion(null)}
                disabled={isDeleting}
              >
                Batal
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={isDeleting}
              >
                {isDeleting ? "Menghapus..." : "Hapus"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Stack>
    </PageContainer>
  );
};
